import React, { useState } from 'react';
import * as Sentry from '@sentry/nextjs';
import toast from 'react-hot-toast';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/bootstrap.css';
import { PostRequestPayload } from 'constants/payloads/tournamentsRegister';
import { useUpdatePlayerPhoneMutation } from 'types/generated/client';
import { getEventExperience } from 'utils/shared/events/getEventExperience';
import { EventCoupon, OrderTotal } from 'utils/shared/money/calculateEventOrderTotal';
import { stripDialCode } from 'utils/shared/phone/stripDialCode';
import { toDigits } from 'utils/shared/phone/toDigits';
import { toFormattedPhone } from 'utils/shared/phone/toFormatedPhone';
import { toSafePhone } from 'utils/shared/phone/toSafePhone';
import { useApiGateway } from 'hooks/useApi';
import { Button } from 'components/Button';
import InfoAlert from 'components/InfoAlert';
import ContactInfoNotice from './ContactInfoNotice';
import DiscountCode from './DiscountCode';
import OrderConfirmationList from './OrderConfirmationList';
import OrderItemList from './OrderItemList';
import { REGISTER_API, RegisterProps, RegistrationFormData } from './types';

interface Props extends RegisterProps {
  onSubmit: () => void | Promise<void>;
  back?: () => void;
  orderTotal: OrderTotal;
  registrationFormData: RegistrationFormData;
  invitationId?: string;
  teamId?: string;
  appliedCoupon: EventCoupon | null;
  couponError?: string | null;
  onApplyCoupon?: (code: string) => void;
  onClearCoupon: () => void;
}

export default function ConfirmWithoutPayment({
  onSubmit,
  back,
  event: tournament,
  orderTotal,
  registrationFormData,
  invitationId,
  teamId,
  user,
  appliedCoupon,
  couponError,
  onApplyCoupon,
  onClearCoupon,
}: Props) {
  const [isLoading, setIsLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [dialCode, setDialCode] = useState('');
  const { post: createTournamentPayment } = useApiGateway<PostRequestPayload>(REGISTER_API);
  const [updatePlayerPhoneMutation] = useUpdatePlayerPhoneMutation();
  const experience = getEventExperience(tournament);
  const eventNoun = experience.noun;
  // Racket Social's intake answers only exist on its own registrations.
  const isSocialEvent = experience.kind === 'social';

  const handlePhoneNumberChange = (phone: string, country: { dialCode: string } & object): void => {
    setDialCode(`${toSafePhone(country.dialCode)}`);
    setPhoneNumber(toFormattedPhone(phone));
  };
  const isPrivateWithOutCost = tournament?.isQuickCreate || !orderTotal?.total ? true : false;

  return (
    <form
      onSubmit={async (event) => {
        event.preventDefault();

        setIsLoading(true);

        try {
          const registrationResponse = await createTournamentPayment({
            payload: {
              isPayAtEvent: true,
              tournamentId: tournament?.id,
              duprId: registrationFormData.duprId,
              birthday: registrationFormData.birthday,
              groups: registrationFormData.groups,
              invitationId: invitationId,
              teamId: teamId,
              couponId: appliedCoupon?.id,
              referredBy: isSocialEvent
                ? registrationFormData.referredBy?.trim() || undefined
                : undefined,
              skillLevelId: isSocialEvent
                ? registrationFormData.skillLevelId || undefined
                : undefined,
            },
          });

          if (registrationResponse.isError) {
            toast.error(
              registrationResponse.error || `There was an error registering for the ${eventNoun}.`,
            );
            setIsLoading(false);
            return;
          }

          const nationalNumber = stripDialCode(phoneNumber, dialCode);
          if (user?.id && nationalNumber && dialCode) {
            const playerVariables = {
              id: user.id,
              phoneNumber: nationalNumber,
              phoneCountryCode: toDigits(dialCode),
            };
            await updatePlayerPhoneMutation({
              variables: playerVariables,
            });
          }

          await onSubmit();

          setIsLoading(false);
        } catch (error) {
          Sentry.captureException(error);
          toast.error(
            `There was an error signing up for the ${eventNoun}. Refresh the page and try again.`,
          );
          setIsLoading(false);
        } finally {
          setIsLoading(false);
        }
      }}
    >
      <div className="flex flex-auto flex-col px-6 pb-6">
        <InfoAlert title="Pay at event via cash or check" className="mb-ds-xl" />
        {!isPrivateWithOutCost && (
          <div>
            {onApplyCoupon && (
              <div className="mb-4">
                <DiscountCode
                  appliedCoupon={appliedCoupon}
                  onApply={onApplyCoupon}
                  onClear={onClearCoupon}
                  error={couponError}
                />
              </div>
            )}
            <OrderItemList orderTotal={orderTotal} appliedCoupon={appliedCoupon} />
          </div>
        )}

        {isPrivateWithOutCost && <div>{<OrderConfirmationList orderTotal={orderTotal} />}</div>}

        <div className="mt-6">
          <label className="typography-product-subheading mb-ds-lg block">Phone Number</label>
          <PhoneInput
            inputProps={{
              name: 'phone',
              required: true,
            }}
            country={'us'}
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            inputClass={'!w-full !py-3'}
          />
          <ContactInfoNotice />
        </div>
      </div>

      <div className="sticky bottom-0 mx-auto w-full flex-row items-center justify-center  bg-color-bg-lightmode-primary p-4 dark:border-color-border-input-darkmode dark:bg-color-bg-darkmode-primary lg:flex">
        <Button disabled={isLoading} type="submit" variant="brand" size="lg">
          {isLoading ? 'Loading...' : 'Register'}
        </Button>
      </div>
    </form>
  );
}
